import { CLIENT_TOKEN_REFRESH_PATH } from '@/config/env';
import type { UnauthorizedReason } from '@/services/httpClient';
import { readStoredSession, writeStoredSession } from '@/services/sessionStorage';

import { toSession } from './session';

let inFlight: Promise<string | null> | null = null;

async function refreshSession(): Promise<string | null> {
  const current = readStoredSession();
  if (!current?.refreshToken) {
    writeStoredSession(null);
    return null;
  }

  try {
    const response = await fetch(CLIENT_TOKEN_REFRESH_PATH, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: current.refreshToken }),
    });
    const next = toSession(response.ok ? await response.json() : null);

    if (!next.token) {
      // Refresh token expired or was revoked: the only way back is a new sign-in.
      if (response.status === 400 || response.status === 401 || response.status === 403) writeStoredSession(null);
      return null;
    }

    writeStoredSession({
      token: next.token,
      // SimpleJWT only returns a new refresh token when rotation is on.
      refreshToken: next.refreshToken ?? current.refreshToken,
      user: next.user ?? current.user,
    });
    return next.token;
  } catch {
    // Offline / server down: keep the session so the user can retry.
    return null;
  }
}

/**
 * Refreshes the stored session. Concurrent 401s share one refresh call, so a page
 * that fires several requests at once does not spend the refresh token twice.
 */
export function refreshSessionOnce(): Promise<string | null> {
  inFlight ??= refreshSession().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** The handler `request()` calls on a 401 from an authenticated call. */
export function handleUnauthorized(reason: UnauthorizedReason): Promise<string | null> {
  if (reason === 'ended') {
    writeStoredSession(null);
    return Promise.resolve(null);
  }
  return refreshSessionOnce();
}
